// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getPageLinkByNodeId, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setShowDropShadow from "@rc/App/Handlers/XNG/showDropShadow";
import Tooltip from "@rc/MobiusOverrides/Tooltip/Tooltip";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

const enum fields {
    title = "title",
    url = "url",
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.url]: LinkFieldValue;
        [fields.links]: CustomLinkModel[];
    };
}
export interface CustomLinkModel {
    fields: {
        destination: LinkFieldValue;
        value: string;
        overriddenTitle: string;
    };
}

const mapDispatchToProps = {
    setShowDropShadow,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;
export interface contactMenuStyles {
    container?: InjectableCss;
    destination?: LinkPresentationProps;
    value?: LinkPresentationProps;
    innerContainer?: InjectableCss;
    labelWrapper?: ClickablePresentationProps;
}

export const contactMenuStyles: contactMenuStyles = {
    container: {
        css: css``,
    },
    innerContainer: {
        css: css`
            display: flex;
            justify-content: space-between;
            margin-bottom: 12px;
            &:last-child {
                margin-bottom: 0;
            }
        `,
    },
    destination: {
        color: "common.ui10",
        typographyProps: {
            size: 14,
            css: css`
                line-height: 16px;
                font-weight: 500;
            `,
        },
    },
    value: {
        color: "primary",
        typographyProps: {
            size: 14,
            css: css`
                line-height: 16px;
                font-weight: 500;
            `,
        },
    },
    labelWrapper: {
        css: css`
            align-items: end;
        `,
    },
};

const styles = contactMenuStyles;

const contactMenu = ({ id, fields: { title, url, links }, setShowDropShadow }: Props) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const linkUrl = useGetLink(url);

    const ContactMenuElement = (
        <StyledWrapper className="contactMenu-element">
            <Clickable
                data-test-selector="header_contact"
                aria-label="sign in"
                href={linkUrl?.url}
                {...styles.labelWrapper}
            >
                <Icon
                    size={18}
                    color="common.ui4"
                    src="PhoneInactive"
                    className={isOpen ? `opened` : ""}
                    css={css`
                        margin-right: 8px;
                    `}
                />
                <Typography
                    size={16}
                    lineHeight="20px"
                    color="text"
                    css={css`
                        font-weight: 500;
                        &.open,
                        &:hover,
                        &:focus {
                            color: ${getColor("primary.main")};
                        }
                    `}
                    className={`title-clickable  ${isOpen && "open"} `}
                >
                    {translate("Contact")}
                </Typography>
            </Clickable>
        </StyledWrapper>
    );

    const ContactMenu = () => (
        <StyledWrapper
            css={css`
                padding: 17px;
                background-color: ${getColor("common.ui1")};
            `}
        >
            <Typography
                size={16}
                lineHeight="24px"
                color="common.ui6"
                css={css`
                    font-weight: 500;
                    display: block;
                    border-bottom: 1px solid ${getColor("common.ui3")};
                    margin-bottom: 12px;
                    padding-bottom: 3px;
                `}
            >
                {title}
            </Typography>
            <StyledWrapper
                css={css`
                    padding-right: 16px;
                `}
            >
                {links?.map((link, index) => {
                    const { destination, value, overriddenTitle } = link.fields;
                    const linkValue = useGetLink(destination);
                    if (!linkValue?.url || (linkValue as any).excludeFromNavigation) {
                        return null;
                    }
                    return (
                        <StyledWrapper {...styles.innerContainer} key={index}>
                            <Link {...styles.destination} href={linkValue.url}>
                                {overriddenTitle || linkValue.title}
                            </Link>
                            <Link href={`tel:${value}`} {...styles.value}>
                                {value}
                            </Link>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );

    return (
        <StyledWrapper className="contactMenu" {...styles.container}>
            <Tooltip
                trigger="mouseenter focus"
                onOpen={() => {
                    hideAll();
                    setIsOpen(true);
                }}
                onClose={() => {
                    setIsOpen(false);
                }}
                placement="bottom-start"
                triggerAltText={translate("contact menu")}
                triggerComponent={ContactMenuElement}
                delay={[100, 350]}
                offset={[0, 8]}
                cssOverrides={{
                    tooltipBody: css``,
                    tooltipContainer: css`
                        &&&& {
                            max-width: initial;
                            width: 300px;
                            padding: 0;
                            box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
                            border-radius: 0;
                            border: 1px solid ${getColor("common.ui3")};
                            overflow: hidden;
                            margin-top: 11px;
                            .tippy-arrow {
                                top: 5px;
                            }
                        }
                    `,
                }}
                passAsChildren={true}
            >
                <ContactMenu />
            </Tooltip>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(contactMenu),
    definition: {
        group: "XNGAGE" as any,
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.url,
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                fieldType: "General",
                isRequired: false,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const { overriddenTitle } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                    if (type === "Category") {
                        if (overriddenTitle) {
                            return overriddenTitle;
                        }
                        if (value === emptyGuid) {
                            return "Products";
                        }
                        const categoryState = getCategoryState(state, value);
                        if (!categoryState.value) {
                            if (!categoryState.isLoading && value) {
                                return dispatch => {
                                    dispatch(
                                        loadCategory({
                                            id: value,
                                        }),
                                    );
                                };
                            }
                            return "";
                        }
                        return categoryState.value.shortDescription;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "value",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
